import { PDFDocument, StandardFonts, PageSizes } from "pdf-lib";
import { calculatePrayerTimes, monthlyPrayerTimes, PRAYER_LABELS_ID, PRAYER_KEYS, type PrayerKey } from "@/lib/prayer/calc";
import { type Location, tzOffsetHours, tzLabel, getProvinsi } from "@/data/locations";
import { MONTH_NAMES_ID, DAY_NAMES_SHORT_ID, dayOfWeek, parseISO } from "@/lib/calendar/dates";
import { gregorianToHijri, HIJRI_MONTHS } from "@/lib/calendar/hijri";
import { getHijriEvent } from "@/lib/calendar/hijri-events";
import { GRAY, TEXT, LIGHT, GREEN, type BaseOpts, embedLogo, drawBrandHeader, drawFooter } from "./pdf-shared";
import { displayLocationTitle } from "@/lib/prayer/copy";
import { rgb } from "pdf-lib";

const PRAYER_GREEN = rgb(0.16, 0.46, 0.30);

/** Reserved vertical space at bottom of every page for drawFooter() block. */
const FOOTER_RESERVE = 100;

const SHOLAT_COLS: PrayerKey[] = ["imsak", "subuh", "terbit", "dhuha", "dzuhur", "ashar", "maghrib", "isya"];
const IMSAK_COLS: PrayerKey[] = ["imsak", "subuh", "terbit", "dzuhur", "ashar", "maghrib", "isya"];
const BUKA_PUASA_COLS: PrayerKey[] = ["imsak", "subuh", "maghrib", "isya"];

export type PrayerVariant = "sholat" | "imsak" | "buka-puasa";

interface PrayerPdfOpts extends BaseOpts {
  variant: PrayerVariant;
  location: Location;
}

function brandTitle(variant: PrayerVariant) {
  if (variant === "sholat") return "Jadwal Sholat Indonesia";
  if (variant === "imsak") return "Jadwal Imsakiyah Indonesia";
  return "Jadwal Buka Puasa Indonesia";
}
function pageHeading(variant: PrayerVariant) {
  if (variant === "sholat") return "Jadwal Sholat";
  if (variant === "imsak") return "Jadwal Imsakiyah";
  return "Jadwal Buka Puasa";
}
function colsFor(variant: PrayerVariant) {
  if (variant === "sholat") return SHOLAT_COLS;
  if (variant === "imsak") return IMSAK_COLS;
  return BUKA_PUASA_COLS;
}

/** One day — A4 portrait, big cards. */
export async function generatePrayerDayPDF(y: number, m: number, d: number, opts: PrayerPdfOpts): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage(PageSizes.A4);
  const W = page.getWidth(), H = page.getHeight();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);
  const { location, variant } = opts;
  const tzH = tzOffsetHours(location.tz);
  const times = calculatePrayerTimes({ y, m, d, lat: location.lat, lng: location.lng, tzHours: tzH });
  const cols = colsFor(variant);
  const hijri = gregorianToHijri(y, m, d);
  const provName = getProvinsi(location.provinceSlug)?.name ?? "";

  const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 22, brandTitle(variant));
  page.drawText(`${pageHeading(variant)} ${displayLocationTitle(location)}`, { x: 30, y: brandBottom - 24, size: 22, font: bold, color: TEXT });
  page.drawText(`${DAY_NAMES_SHORT_ID[dayOfWeek(y, m, d)]}, ${d} ${MONTH_NAMES_ID[m - 1]} ${y} - ${hijri.day} ${HIJRI_MONTHS[hijri.month - 1]} ${hijri.year} H`, { x: 30, y: brandBottom - 42, size: 11, font, color: GRAY });
  page.drawText(`${location.kind === "kecamatan" ? "Kec. " : ""}${location.name}${location.kind !== "provinsi" ? ", " + provName : ""} - ${tzLabel(location.tz)} (${location.lat.toFixed(3)}, ${location.lng.toFixed(3)})`, { x: 30, y: brandBottom - 56, size: 9, font, color: GRAY });

  // Big cards
  const cardTop = brandBottom - 90;
  const padX = 30;
  const gap = 10;
  const perRow = 4;
  const rows = Math.ceil(cols.length / perRow);
  const cardW = (W - padX * 2 - gap * (perRow - 1)) / perRow;
  const cardH = 90;
  cols.forEach((k, i) => {
    const col = i % perRow;
    const row = Math.floor(i / perRow);
    const x = padX + col * (cardW + gap);
    const y = cardTop - row * (cardH + gap);
    page.drawRectangle({ x, y: y - cardH, width: cardW, height: cardH, color: LIGHT, borderWidth: 0.5, borderColor: LIGHT });
    page.drawText(PRAYER_LABELS_ID[k].toUpperCase(), { x: x + 10, y: y - 22, size: 9, font: bold, color: GRAY });
    page.drawText(times[k], { x: x + 10, y: y - 60, size: 28, font: bold, color: PRAYER_GREEN });
  });

  // Method note
  const computedNoteY = cardTop - rows * (cardH + gap) - 20;
  const noteY = Math.max(computedNoteY, FOOTER_RESERVE + 24);
  const dayEvent = getHijriEvent(hijri.month, hijri.day);
  if (dayEvent) {
    page.drawText(`* ${dayEvent.name} - ${dayEvent.descId}`, {
      x: 30, y: noteY + 14, size: 8, font: bold, color: PRAYER_GREEN, maxWidth: W - 60,
    });
  }
  page.drawText("Metode perhitungan: Kementerian Agama RI (Subuh 20°, Isya 18°, Asar Syafi'i, ihtiyat 2 menit, Imsak = Subuh - 10 menit). Akurasi +/- 1 menit.", { x: 30, y: noteY, size: 8, font, color: GRAY, maxWidth: W - 60 });

  drawFooter(page, font, opts.siteUrl ?? `https://hari.co.id/jadwal-${variant}/${location.slug}/`);
  return doc.save();
}

/** Whole month — A4 portrait table. Paginates if rows would collide with footer. */
export async function generatePrayerMonthPDF(y: number, m: number, opts: PrayerPdfOpts): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);
  const { location, variant } = opts;
  const tzH = tzOffsetHours(location.tz);
  const cols = colsFor(variant);

  const month = monthlyPrayerTimes({ y, m, lat: location.lat, lng: location.lng, tzHours: tzH });
  const tableW0 = PageSizes.A4[0] - 60;
  const dateColW = 130;
  const timeColW = (tableW0 - dateColW) / cols.length;
  const rowH = 20;

  let page = doc.addPage(PageSizes.A4);
  let pageNum = 1;
  const totalPages = (() => {
    // first page: header takes ~76pt, plus 18pt header row + N * 14pt rows must fit above FOOTER_RESERVE
    const usableFirst = PageSizes.A4[1] - 76 - 18 - FOOTER_RESERVE;
    const usableNext = PageSizes.A4[1] - 40 - 18 - FOOTER_RESERVE;
    const firstFits = Math.floor(usableFirst / rowH);
    if (month.length <= firstFits) return 1;
    return 1 + Math.ceil((month.length - firstFits) / Math.floor(usableNext / rowH));
  })();

  function startPage(first: boolean) {
    const W = page.getWidth(), H = page.getHeight();
    const left = 30;
    let yCursor: number;
    if (first) {
      const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 22, brandTitle(variant));
      page.drawText(`${pageHeading(variant)} ${displayLocationTitle(location)}`, { x: 30, y: brandBottom - 22, size: 18, font: bold, color: TEXT });
      const dim = month.length;
      const rangeText = `Periode 1 - ${dim} ${MONTH_NAMES_ID[m - 1]} ${y}`;
      page.drawText(rangeText, { x: 30, y: brandBottom - 38, size: 10, font: bold, color: PRAYER_GREEN });
      page.drawText(`${tzLabel(location.tz)} (${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}) - Halaman ${pageNum}/${totalPages}`, { x: 30, y: brandBottom - 52, size: 9, font, color: GRAY });
      yCursor = brandBottom - 70;
    } else {
      page.drawText(`${pageHeading(variant)} ${displayLocationTitle(location)} — ${MONTH_NAMES_ID[m - 1]} ${y} (lanjutan)`, { x: 30, y: H - 30, size: 11, font: bold, color: TEXT });
      page.drawText(`Halaman ${pageNum}/${totalPages}`, { x: W - 90, y: H - 30, size: 9, font, color: GRAY });
      yCursor = H - 46;
    }
    // Table header
    page.drawRectangle({ x: left, y: yCursor - 18, width: tableW0, height: 18, color: LIGHT });
    page.drawText("Tanggal", { x: left + 6, y: yCursor - 13, size: 8, font: bold, color: TEXT });
    cols.forEach((k, i) => {
      const cx = left + dateColW + i * timeColW + timeColW / 2;
      page.drawText(PRAYER_LABELS_ID[k], { x: cx - bold.widthOfTextAtSize(PRAYER_LABELS_ID[k], 8) / 2, y: yCursor - 13, size: 8, font: bold, color: TEXT });
    });
    return yCursor - 18;
  }

  let rowY = startPage(true);
  for (let i = 0; i < month.length; i++) {
    if (rowY - rowH < FOOTER_RESERVE) {
      // close out current page
      page.drawText(`Bersambung ke halaman ${pageNum + 1}/${totalPages} - hari.co.id`, { x: 30, y: FOOTER_RESERVE - 12, size: 7, font, color: GRAY });
      drawFooter(page, font, opts.siteUrl ?? `https://hari.co.id/jadwal-${variant}/${location.slug}/`);
      page = doc.addPage(PageSizes.A4);
      pageNum++;
      rowY = startPage(false);
    }
    rowY -= rowH;
    const r = month[i];
    const p = parseISO(r.iso)!;
    const dn = DAY_NAMES_SHORT_ID[dayOfWeek(p.y, p.m, p.d)];
    const h = gregorianToHijri(p.y, p.m, p.d);
    const ev = getHijriEvent(h.month, h.day);
    const isFri = dayOfWeek(p.y, p.m, p.d) === 5;
    const isSun = dayOfWeek(p.y, p.m, p.d) === 0;
    if ((p.d % 2) === 0) page.drawRectangle({ x: 30, y: rowY, width: tableW0, height: rowH, color: rgb(0.97, 0.98, 0.97) });
    page.drawText(`${dn}, ${p.d} ${MONTH_NAMES_ID[p.m - 1]}`, { x: 36, y: rowY + rowH - 9, size: 8, font, color: isSun ? rgb(0.74, 0.22, 0.22) : isFri ? PRAYER_GREEN : TEXT });
    const hijriLine = `${h.day} ${HIJRI_MONTHS[h.month - 1]}`;
    page.drawText(hijriLine, { x: 36, y: rowY + 3, size: 6.5, font, color: GRAY });
    if (ev) {
      const xEv = 36 + font.widthOfTextAtSize(hijriLine, 6.5) + 6;
      page.drawText(`* ${ev.name}`, { x: xEv, y: rowY + 3, size: 6.5, font: bold, color: PRAYER_GREEN, maxWidth: PageSizes.A4[0] - 60 - (xEv - 30) });
    }
    cols.forEach((k, ci) => {
      const cx = 30 + dateColW + ci * timeColW + timeColW / 2;
      const t = r.times[k];
      page.drawText(t, { x: cx - font.widthOfTextAtSize(t, 8) / 2, y: rowY + rowH / 2 - 3, size: 8, font, color: TEXT });
    });
  }

  // Final-page method note + footer (above the FOOTER_RESERVE band)
  page.drawText("* = hari besar / penting Islam (Nuzulul Quran, Idul Fitri, dll.)   ·   Metode: Kementerian Agama RI (Subuh 20°, Isya 18°, Asar Syafi'i, ihtiyat 2 menit). Sumber: bimasislam.kemenag.go.id", { x: 30, y: FOOTER_RESERVE - 12, size: 7, font, color: GRAY, maxWidth: PageSizes.A4[0] - 60 });
  drawFooter(page, font, opts.siteUrl ?? `https://hari.co.id/jadwal-${variant}/${location.slug}/`);
  return doc.save();
}

/** Whole year — A4 landscape, 12 mini tables across 4 pages (3 months/page). */
export async function generatePrayerYearPDF(y: number, opts: PrayerPdfOpts): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);
  const { location, variant } = opts;
  const tzH = tzOffsetHours(location.tz);
  const cols = colsFor(variant);

  // 4 pages × 3 months
  for (let pageIdx = 0; pageIdx < 4; pageIdx++) {
    const page = doc.addPage([PageSizes.A4[1], PageSizes.A4[0]]);
    const W = page.getWidth(), H = page.getHeight();
    const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 18, brandTitle(variant));
    page.drawText(`${pageHeading(variant)} ${displayLocationTitle(location)} - ${y}`, { x: 30, y: brandBottom - 20, size: 14, font: bold, color: TEXT });
    page.drawText(`Periode 1 Jan - 31 Des ${y}`, { x: 30, y: brandBottom - 34, size: 9, font: bold, color: PRAYER_GREEN });
    page.drawText(`${tzLabel(location.tz)} (${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}) - Halaman ${pageIdx + 1}/4`, { x: W - 240, y: brandBottom - 20, size: 8, font, color: GRAY });

    // 3 columns of month tables
    const padX = 20, gap = 12;
    const colW = (W - padX * 2 - gap * 2) / 3;
    for (let i = 0; i < 3; i++) {
      const m = pageIdx * 3 + i + 1;
      if (m > 12) break;
      const x = padX + i * (colW + gap);
      const yTop = brandBottom - 54;
      const month = monthlyPrayerTimes({ y, m, lat: location.lat, lng: location.lng, tzHours: tzH });
      page.drawText(`${MONTH_NAMES_ID[m - 1]} ${y}`, { x: x + 4, y: yTop - 10, size: 10, font: bold, color: PRAYER_GREEN });
      const tHeadY = yTop - 22;
      const dateColW = 38;
      const timeColW = (colW - dateColW) / cols.length;
      page.drawRectangle({ x, y: tHeadY - 12, width: colW, height: 12, color: LIGHT });
      page.drawText("Tgl", { x: x + 3, y: tHeadY - 9, size: 6, font: bold, color: TEXT });
      cols.forEach((k, ci) => {
        const lab = PRAYER_LABELS_ID[k];
        const cx = x + dateColW + ci * timeColW + timeColW / 2;
        page.drawText(lab, { x: cx - bold.widthOfTextAtSize(lab, 6) / 2, y: tHeadY - 9, size: 6, font: bold, color: TEXT });
      });
      let ry = tHeadY - 12;
      // Clamp rowH so the last day never overlaps the footer band.
      const usable = (tHeadY - 12) - FOOTER_RESERVE;
      const rowH = Math.max(7, usable / 31);
      for (const r of month) {
        ry -= rowH;
        if (ry < FOOTER_RESERVE) break;
        const p = parseISO(r.iso)!;
        if (p.d % 2 === 0) page.drawRectangle({ x, y: ry, width: colW, height: rowH, color: rgb(0.97, 0.98, 0.97) });
        page.drawText(String(p.d), { x: x + 3, y: ry + rowH / 2 - 2.5, size: 6, font, color: TEXT });
        cols.forEach((k, ci) => {
          const cx = x + dateColW + ci * timeColW + timeColW / 2;
          const t = r.times[k];
          page.drawText(t, { x: cx - font.widthOfTextAtSize(t, 6) / 2, y: ry + rowH / 2 - 2.5, size: 6, font, color: TEXT });
        });
      }
    }
    drawFooter(page, font, opts.siteUrl ?? `https://hari.co.id/jadwal-${variant}/${location.slug}/`);
  }
  return doc.save();
}

void GREEN; void PRAYER_KEYS;
